export type ConnectionState = "connecting" | "streaming" | "done" | "error"

export type SSEEvent = {
  event: string
  data: string
}

export type SSEClientOptions = {
  url: string
  body: Record<string, unknown>
  token: string
  signal?: AbortSignal
  onStateChange?: (_state: ConnectionState) => void
}

function parseSSEFrame(frame: string): SSEEvent | null {
  let event = "message"
  const dataLines: string[] = []

  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim()
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (dataLines.length === 0) return null

  return { event, data: dataLines.join("\n") }
}

export async function* streamSSE(options: SSEClientOptions): AsyncGenerator<SSEEvent> {
  const { url, body, token, signal, onStateChange } = options

  const setState = (state: ConnectionState) => {
    onStateChange?.(state)
  }

  setState("connecting")

  let response: Response

  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token jwt="${token}"`,
      },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      setState("done")
      return
    }
    setState("error")
    throw err
  }

  if (!response.ok) {
    setState("error")
    throw new Error(`SSE request failed: ${response.status}`)
  }

  if (!response.body) {
    setState("error")
    throw new Error("Response body is null")
  }

  setState("streaming")

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  try {
    while (true) {
      let result: { done: boolean; value?: Uint8Array }

      try {
        result = await reader.read()
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          setState("done")
          return
        }
        if (err instanceof TypeError) {
          setState("error")
          throw err
        }
        setState("error")
        throw err
      }

      if (result.done) break

      buffer += decoder.decode(result.value, { stream: true })

      const frames = buffer.split("\n\n")
      buffer = frames.pop()!

      for (const frame of frames) {
        const trimmed = frame.trim()
        if (!trimmed) continue

        const parsed = parseSSEFrame(trimmed)
        if (parsed) yield parsed
      }
    }

    if (buffer.trim()) {
      const parsed = parseSSEFrame(buffer.trim())
      if (parsed) yield parsed
    }

    setState("done")
  } finally {
    reader.releaseLock()
  }
}
